<template>
    <div>
        <div class="flex flex-wrap mt-5">
            <div class="w-full md:w-1/2 mt-2 px-2 flex flex-col my-2">
                <custom-input
                    id="name"
                    v-model="itemComponent.firstName"
                    class="w-full"
                    label="Nombres"
                    :error="
                        !isValidFirstName && introValidators.firstName
                            ? 'Nombres no validos'
                            : ''
                    "
                    @blur="introValidators.firstName = true"
                />
            </div>

            <div class="w-full md:w-1/2 mt-2 px-2 flex flex-col my-2">
                <custom-input
                    v-model="itemComponent.lastName"
                    class="w-full"
                    label="Apellidos"
                    :error="
                        !isValidLastName && introValidators.lastName
                            ? 'Apellidos no validos'
                            : ''
                    "
                    @blur="introValidators.lastName = true"
                />
            </div>

            <div class="w-full md:w-1/2 mt-2 px-2 flex flex-col my-2">
                <custom-input
                    id="phone"
                    v-model="itemComponent.phone"
                    class="w-full"
                    label="Celular de contacto"
                    type="number"
                    :error="
                        !isValidPhone && introValidators.phone
                            ? 'Celular no valido'
                            : ''
                    "
                    @blur="introValidators.phone = true"
                />
            </div>

            <div class="w-full md:w-1/2 mt-2 px-2 flex flex-col my-2">
                <custom-input
                    v-model="itemComponent.email"
                    class="w-full"
                    label="Email (Optional)"
                    :error="
                        !isValidEmail && introValidators.email
                            ? 'Email no es valido'
                            : ''
                    "
                    @blur="introValidators.email = true"
                />
            </div>

            <div class="w-full mt-2 px-2">
                <custom-checkbox v-model="needIdentification" warn>
                    Agregar identificación de cliente
                </custom-checkbox>

                <custom-collapse :visible="needIdentification">
                    <div class="flex flex-wrap mt-3">
                        <div class="w-full md:w-1/2 px-2 flex flex-col my-2">
                            <identification-type-selector
                                v-model="itemComponent.identificationType"
                                enabled
                            />
                        </div>

                        <div class="w-full md:w-1/2 px-2 flex flex-col my-2">
                            <custom-input
                                v-model="itemComponent.identificationNumber"
                                class="w-full"
                                label="Número de identificación"
                                :error="
                                    !isValidIdentificationNumber &&
                                    introValidators.identificationNumber
                                        ? 'Numero de identificación no valido'
                                        : ''
                                "
                                @blur="
                                    introValidators.identificationNumber = true
                                "
                            />
                        </div>
                    </div>
                </custom-collapse>
            </div>

            <div class="w-full mt-2 px-2">
                <custom-checkbox v-model="needAddress" warn>
                    Agregar dirección de cliente
                </custom-checkbox>

                <custom-collapse :visible="needAddress">
                    <div class="flex flex-wrap mt-3">
                        <div class="w-full md:w-1/2 px-2 flex flex-col my-2">
                            <custom-input
                                id="address"
                                v-model="itemComponent.address"
                                class="w-full"
                                label="Dirección"
                                :error="
                                    !isValidAddress && introValidators.address
                                        ? 'Dirección no es valida'
                                        : ''
                                "
                                @blur="onBlurAddress"
                            />
                        </div>

                        <div class="w-full md:w-1/2 px-2 flex flex-col my-2">
                            <city-code-selector
                                v-model="itemComponent.cityCode"
                                enabled
                            />
                        </div>

                        <div class="w-full md:w-1/2 px-2 flex flex-col my-2">
                            <custom-input
                                v-model="itemComponent.addressComplement"
                                class="w-full"
                                label="Complemento de dirección"
                            />
                        </div>

                        <div class="w-full md:w-1/2 px-2 flex flex-col my-2">
                            <custom-input
                                v-model="itemComponent.neighborhood"
                                class="w-full"
                                label="Barrio"
                            />
                        </div>
                    </div>
                </custom-collapse>
            </div>

            <p class="text-xs text-theme-muted text-center">
                Antes de registrar a un cliente, asegúrate de tener su
                autorización para tratar sus datos.
            </p>
            <div class="flex justify-center my-4 w-8/12 mx-auto">
                <app-button size="large" @click="createCustomerComponent">
                    Crear cliente
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import {
    addressRegex,
    emailRegex,
    identificationNumberRegex,
    nameRegex,
    phoneRegex
} from "@/common/lib/regex";

import IdentificationTypeSelector from "@/components/users/IdentificationTypeSelector.vue";
import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CustomCollapse from "@/components/utils/CustomCollapse.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "CreateCustomer",
    components: {
        IdentificationTypeSelector,
        CityCodeSelector,
        CustomCollapse,
        CustomInput
    },
    emits: ["create"],
    data: () => ({
        itemComponent: {
            identificationType: "cc",
            identificationNumber: "",
            firstName: "",
            lastName: "",
            cityCode: "11001",
            address: "",
            addressComplement: "",
            neighborhood: "",
            email: "",
            phone: ""
        },
        introValidators: {
            email: false,
            phone: false,
            identificationNumber: false,
            firstName: false,
            lastName: false,
            address: false
        },
        needAddress: false,
        needIdentification: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isValidEmail() {
            return (
                emailRegex.test(this.itemComponent.email) ||
                this.itemComponent.email === ""
            );
        },
        isValidIdentificationNumber() {
            return (
                identificationNumberRegex.test(
                    this.itemComponent.identificationNumber
                ) || this.itemComponent.identificationNumber === ""
            );
        },
        isValidFirstName() {
            return (
                this.itemComponent.firstName !== "" &&
                nameRegex.test(this.itemComponent.firstName)
            );
        },
        isValidLastName() {
            return (
                this.itemComponent.lastName !== "" &&
                nameRegex.test(this.itemComponent.lastName)
            );
        },
        isValidAddress() {
            return addressRegex.test(this.itemComponent.address);
        },
        isValidPhone() {
            return phoneRegex.test(this.itemComponent.phone);
        }
    },
    methods: {
        createCustomerComponent() {
            if (!this.validateData()) {
                return;
            }
            this.$emit("create", {
                phone: this.itemComponent.phone,
                firstName: this.itemComponent.firstName,
                lastName: this.itemComponent.lastName,
                email: this.itemComponent.email,
                ...(this.needIdentification
                    ? {
                          identificationNumber:
                              this.itemComponent.identificationNumber,
                          identificationType:
                              this.itemComponent.identificationType
                      }
                    : null),
                ...(this.needAddress
                    ? {
                          location: {
                              address: this.itemComponent.address,
                              addressComplement:
                                  this.itemComponent.addressComplement,
                              cityCode: this.itemComponent.cityCode,
                              neighborhood: this.itemComponent.neighborhood,
                              receiverPhone: this.itemComponent.phone,
                              receiverName: `${this.itemComponent.firstName} ${this.itemComponent.lastName}`
                          }
                      }
                    : null)
            });
        },
        validateData() {
            this.introValidators = {
                identificationNumber: true,
                firstName: true,
                lastName: true,
                address: true,
                email: true,
                phone: true
            };

            const toComplete = [];
            if (!this.isValidFirstName) {
                toComplete.push("Nombre");
            }

            if (!this.isValidLastName) {
                toComplete.push("Apellido");
            }

            if (!this.isValidEmail) {
                toComplete.push("Email");
            }

            if (this.needIdentification && !this.isValidIdentificationNumber) {
                toComplete.push("Número de identificación");
            }

            if (!this.isValidPhone) {
                toComplete.push("Teléfono");
            }

            if (this.needAddress && !this.isValidAddress) {
                toComplete.push("Dirección");
            }

            if (toComplete.length) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Cliente invalido",
                    text: `Revisa por favor que todo este completo. Valida los siguientes campos: ${toComplete.join(
                        ", "
                    )}`,
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        onBlurAddress() {
            this.introValidators.address = true;
            this.itemComponent.address = this.itemComponent.address.trim();
        }
    }
};
</script>
